<template>
  <div class="fhr_content_layout_header">
    <h1 class="fhr_page_title">
      <a class="fcom_back_link" @click.prevent="$emit('cancel')">
        <i class="fcom_icon" v-html="icons.arrowLeft" />
        {{ backLinkLabel }}
      </a>
    </h1>
    <div class="fhr_page_actions">
      <button class="el-button fcom_secondary_button" @click="$emit('cancel')">
        {{ __('cancel') }}
      </button>
      <button v-if="!isDraft" class="el-button fcom_secondary_button" :disabled="isSubmitting"
        @click="submitForm('draft')">
        {{ __('save_draft') }}
      </button>
      <button class="el-button fcom_primary_button" :disabled="isSubmitting" @click="submitForm('publish')">
        {{ submitButtonLabel }}
      </button>
    </div>
  </div>

  <div class="fhr_content_layout_body">
    <div v-if="errorMessage" class="fcom_alert fcom_alert_error">{{ errorMessage }}</div>
    <div v-if="isDraft" class="fcom_alert fcom_alert_info">{{ __('editing_draft') }}</div>

    <div class="fcom_layout">
      <div class="fcom_main_content">

        <!-- Event Details -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.clipboard" />
            {{ __('event_details') }}
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">
              {{ __('event_title') }} <span class="fcom_required">*</span>
            </label>
            <input v-model="form.title" type="text" :placeholder="__('enter_event_title')" class="el-input" />
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">{{ __('url_slug') }}</label>
            <div class="fcom_slug_input">
              <span class="fcom_slug_prefix">/events/</span>
              <input v-model="form.slug" type="text" :placeholder="__('event_slug_placeholder')" :disabled="isDraft" />
              <button v-if="!isDraft" type="button" class="fcom_slug_auto" @click="generateSlug">
                {{ __('auto') }}
              </button>
            </div>
            <p v-if="isDraft" class="fcom_slug_locked_note">
              {{ __('slug_locked_draft') }}
            </p>
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">
              {{ __('short_summary') }}
              <span class="fcom_form_hint">{{ __('shown_in_listings') }}</span>
            </label>
            <textarea v-model="form.summary" rows="3" maxlength="200" :placeholder="__('brief_description')"
              class="el-textarea" />
            <div class="fcom_char_count" :class="{ 'is-over': form.summary.length > 200 }">
              {{ form.summary.length }}/200
            </div>
          </div>
        </div>

        <!-- Event Description -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.edit" />
            {{ __('event_description') }}
          </div>
          <TipTapEditor v-model="form.content" :placeholder="__('tell_attendees')" />
        </div>

      </div>

      <div class="fcom_sidebar">

        <!-- Featured Image -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.image" />
            {{ __('featured_image') }}
          </div>

          <div v-if="form.featuredImageUrl" class="fcom_featured_image_has_image">
            <div class="fcom_image_preview">
              <video
                v-if="isFeaturedVideo"
                :src="form.featuredImageUrl"
                class="fcom_featured_preview_video"
                controls
                playsinline
                preload="metadata"
              />
              <img v-else :src="form.featuredImageUrl" :alt="__('featured_image')" />
            </div>
            <p v-if="canPickFromLibrary" class="fcom_featured_image_links">
              <button type="button" class="fcom_featured_image_link" @click="openMediaLibrary">
                {{ __('edit') }}
              </button>
              <span class="fcom_featured_image_sep" aria-hidden="true">·</span>
              <button type="button" class="fcom_featured_image_link_muted" @click="removeFeaturedLocal">
                {{ __('remove_featured') }}
              </button>
            </p>
            <p v-else class="fcom_featured_image_links">
              <label class="fcom_featured_image_link fcom_upload_fallback">
                <input type="file" accept="image/*" style="display: none" @change="handleImageUpload" />
                {{ __('edit') }}
              </label>
            </p>
            <p v-if="isUploadingFallback" class="fcom_loading_text">{{ __('loading') }}</p>
          </div>

          <div v-else>
            <button
              v-if="canPickFromLibrary"
              type="button"
              class="fcom_set_featured_image_btn"
              @click="openMediaLibrary"
            >
              {{ __('set_featured_image') }}
            </button>
            <label v-else class="fcom_image_upload_label fcom_upload_fallback_block">
              <input type="file" accept="image/*" style="display: none" @change="handleImageUpload" />
              <div class="fcom_upload_fallback_inner">
                <i class="fcom_icon" v-html="icons.plus" />
                <span>{{ __('click_to_upload') }}</span>
                <span class="fcom_upload_hint">{{ __('upload_hint') }}</span>
              </div>
            </label>
            <p v-if="!canPickFromLibrary && isUploadingFallback" class="fcom_loading_text">
              {{ __('loading') }}
            </p>
          </div>
        </div>

        <!-- Date & Time -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.calendar" />
            {{ __('date_and_time') }}
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">{{ __('start') }}</label>
            <div class="fcom_datetime_row">
              <input v-model="form.date" type="date" required class="el-input" />
              <input v-model="form.time" type="time" class="el-input" />
            </div>
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">{{ __('end') }}</label>
            <div class="fcom_datetime_row">
              <input v-model="form.endDate" type="date" class="el-input" />
              <input v-model="form.endTime" type="time" class="el-input" />
            </div>
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">{{ __('timezone') }}</label>
            <select v-model="form.timezone" class="el-select">
              <option v-for="(label, key) in filteredTimezones" :key="key" :value="key">
                {{ label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Event Type & Location -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.location" />
            {{ __('event_type_and_location') }}
          </div>

          <div class="fcom_event_type_toggle">
            <button type="button" class="fcom_event_type_btn" :class="{ active: form.eventType === 'in_person' }"
              @click="form.eventType = 'in_person'">
              <i class="fcom_icon" v-html="icons.location" />
              {{ __('in_person') }}
            </button>
            <button type="button" class="fcom_event_type_btn" :class="{ active: form.eventType === 'online' }"
              @click="form.eventType = 'online'">
              <i class="fcom_icon" v-html="icons.monitor" />
              {{ __('online') }}
            </button>
            <button type="button" class="fcom_event_type_btn" :class="{ active: form.eventType === 'hybrid' }"
              @click="form.eventType = 'hybrid'">
              <i class="fcom_icon" v-html="icons.globe" />
              {{ __('hybrid') }}
            </button>
          </div>

          <!-- Venue address (in-person or hybrid) -->
          <div v-if="form.eventType === 'in_person' || form.eventType === 'hybrid'" class="fcom_form_group">
            <label class="fcom_form_label">{{ __('venue_address') }}</label>
            <input v-model="form.location" type="text" :placeholder="__('venue_placeholder')" class="el-input" />
          </div>

          <!-- Online fields (online or hybrid) -->
          <template v-if="form.eventType === 'online' || form.eventType === 'hybrid'">
            <div class="fcom_form_group">
              <label class="fcom_form_label">{{ __('meeting_provider') }}</label>
              <select v-model="form.meetingProvider" class="el-select">
                <option value="">{{ __('select_provider') }}</option>
                <option value="zoom">{{ __('zoom') }}</option>
                <option value="google_meet">{{ __('google_meet') }}</option>
                <option value="microsoft_teams">{{ __('microsoft_teams') }}</option>
                <option value="webex">{{ __('webex') }}</option>
                <option value="other">{{ __('other') }}</option>
              </select>
            </div>

            <div v-if="form.meetingProvider === 'other'" class="fcom_form_group">
              <label class="fcom_form_label">{{ __('other_provider_name') }}</label>
              <input v-model="form.meetingProviderOther" type="text" :placeholder="__('enter_provider_name')"
                class="el-input" />
            </div>

            <div class="fcom_form_group">
              <label class="fcom_form_label">
                {{ __('meeting_url') }}
                <span class="fcom_form_hint">{{ __('join_link_hint') }}</span>
              </label>
              <input v-model="form.eventUrl" type="url" :placeholder="__('https_placeholder')" class="el-input" />
            </div>

            <div class="fcom_form_group">
              <label class="fcom_checkbox_label">
                <input v-model="form.scheduleLinkVisibility" type="checkbox" style="margin-right: 8px" />
                {{ __('schedule_link_visibility') }}
              </label>

              <div v-if="form.scheduleLinkVisibility" class="fcom_link_visibility_options">
                <label class="fcom_form_label">{{ __('show_link_hours_before') }}</label>
                <div class="fcom_hours_row">
                  <input v-model.number="form.linkVisibleHoursBefore" type="number" min="0" max="168"
                    style="width: 80px" class="el-input" />
                  <span class="fcom_form_hint">{{ __('hours_before_event') }}</span>
                </div>
                <p class="fcom_form_hint_block">{{ __('link_visibility_hint') }}</p>
              </div>
            </div>

            <div class="fcom_form_group">
              <label class="fcom_form_label">{{ __('restrict_to_spaces') }}</label>

              <!-- Space dropdown -->
              <div class="fcom_cat_filter" ref="spaceMeetingLinkFilterRef" style="display: block">
                <button type="button" class="fcom_cat_filter_btn" style="width: 100%; justify-content: space-between"
                  @click="spaceMeetingLinkDropdownOpen = !spaceMeetingLinkDropdownOpen">
                  <span>{{ spaceMeetingLinkSelectionLabel }}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"
                    :style="spaceMeetingLinkDropdownOpen ? 'transform:rotate(180deg)' : ''">
                    <path d="M6 8L1.5 3.5h9z" />
                  </svg>
                </button>

                <div v-if="spaceMeetingLinkDropdownOpen" class="fcom_cat_filter_popover">
                  <div class="fcom_cat_filter_header">
                    <span>{{ __('select_spaces') }}</span>
                    <button v-if="form.meetingSpaceIds.length" class="fcom_cat_filter_clear" type="button"
                      @click="form.meetingSpaceIds = []">
                      {{ __('clear_all') }}
                    </button>
                  </div>

                  <div v-if="isLoadingSpaces" class="fcom_loading_text">
                    {{ __('loading') }}
                  </div>
                  <div v-else class="fcom_cat_filter_list">
                    <label v-for="space in spaces" :key="space.id" class="fcom_cat_filter_item">
                      <input v-model="form.meetingSpaceIds" type="checkbox" :value="space.id" />
                      <span class="fcom_cat_filter_check" />
                      <span>{{ space.title }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Selected spaces as tags -->
              <div v-if="form.meetingSpaceIds.length" class="fcom_selected_spaces" style="margin: 15px 0;">
                <span v-for="spaceId in form.meetingSpaceIds" :key="spaceId" style="
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                  padding: 3px 8px;
                  border-radius: 20px;
                  background: var(--fcom-secondary-bg, #F0F2F5);
                  font-size: 12px;
                  color: var(--fcom-primary-text, #050505);
                ">
                  {{ getSpaceTitle(spaceId) }}
                  <button type="button" @click="removeMeetingLinkSpace(spaceId)" style="
                    all: unset;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 1;
                    color: var(--fcom-text-off, #8A8D91);
                  ">
                    ×
                  </button>
                </span>
              </div>

              <div v-if="form.meetingSpaceIds.length" class="fcom_form_group">
                <label class="fcom_form_label">{{ __('meeting_space_member_mode_label') }}</label>
                <select v-model="form.meetingSpaceMemberMode" class="el-select">
                  <option value="any">{{ __('meeting_space_member_mode_any') }}</option>
                  <option value="all">{{ __('meeting_space_member_mode_all') }}</option>
                </select>
                <p class="fcom_form_hint_block">{{ __('meeting_space_member_mode_hint') }}</p>
              </div>

              <p class="fcom_form_hint_block">{{ __('restrict_to_spaces_link_hint') }}</p>
            </div>

          </template>
        </div>

        <!-- Categories -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.bookmark" />
            {{ __('category') }}
          </div>

          <div v-if="isLoadingCategories" class="fcom_loading_text">
            {{ __('loading') }}
          </div>
          <div v-else-if="categories.length === 0 && !showNewCategoryForm" class="fcom_no_categories">
            {{ __('no_categories') }}
          </div>
          <div v-else class="fcom_category_list">
            <label v-for="cat in categories" :key="cat.id" class="fcom_category_item">
              <input v-model="form.categories" type="checkbox" :value="cat.id" />
              <span>{{ cat.name }}</span>
            </label>
          </div>

          <!-- Add new category form -->
          <div v-if="showNewCategoryForm" class="fcom_new_category_form">
            <input v-model="newCategoryName" type="text" class="fcom_form_input fcom_form_input_sm"
              :placeholder="__('category_name')" @keyup.enter="createCategory" />
            <div class="fcom_new_category_actions">
              <button type="button" class="el-button fcom_primary_button el-button--small"
                :disabled="!newCategoryName.trim() || isCreatingCategory" @click="createCategory">
                {{ isCreatingCategory ? __('creating') : __('create') }}
              </button>
              <button type="button" class="el-button fcom_secondary_button el-button--small" @click="cancelNewCategory">
                {{ __('cancel') }}
              </button>
            </div>
          </div>

          <div v-else class="fcom_add_category_btn_wrap">
            <button type="button" class="el-button el-button--text el-button--small"
              @click="showNewCategoryForm = true">
              <i class="fcom_icon" v-html="icons.plus" />
              {{ __('add_new_category') }}
            </button>
          </div>
        </div>

        <!-- Space Settings -->
        <div class="fcom_form_card">
          <div class="fcom_form_card-title">
            <i class="fcom_icon" v-html="icons.users" />
            {{ __('space_settings') }}
          </div>

          <div class="fcom_form_group">
            <label class="fcom_form_label">{{ __('restrict_to_spaces') }}</label>

            <!-- Space dropdown -->
            <div class="fcom_cat_filter" ref="spaceFilterRef" style="display: block">
              <button type="button" class="fcom_cat_filter_btn" style="width: 100%; justify-content: space-between"
                @click="spaceDropdownOpen = !spaceDropdownOpen">
                <span>{{ spaceSelectionLabel }}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"
                  :style="spaceDropdownOpen ? 'transform:rotate(180deg)' : ''">
                  <path d="M6 8L1.5 3.5h9z" />
                </svg>
              </button>

              <div v-if="spaceDropdownOpen" class="fcom_cat_filter_popover">
                <div class="fcom_cat_filter_header">
                  <span>{{ __('select_spaces') }}</span>
                  <button v-if="form.spaceIds.length" class="fcom_cat_filter_clear" type="button"
                    @click="form.spaceIds = []">
                    {{ __('clear_all') }}
                  </button>
                </div>

                <div v-if="isLoadingSpaces" class="fcom_loading_text">
                  {{ __('loading') }}
                </div>
                <div v-else class="fcom_cat_filter_list">
                  <label v-for="space in spaces" :key="space.id" class="fcom_cat_filter_item">
                    <input v-model="form.spaceIds" type="checkbox" :value="space.id" />
                    <span class="fcom_cat_filter_check" />
                    <span>{{ space.title }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Selected spaces as tags -->
            <div v-if="form.spaceIds.length" class="fcom_selected_spaces" style="margin: 15px 0;">
              <span v-for="spaceId in form.spaceIds" :key="spaceId" style="
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                  padding: 3px 8px;
                  border-radius: 20px;
                  background: var(--fcom-secondary-bg, #F0F2F5);
                  font-size: 12px;
                  color: var(--fcom-primary-text, #050505);
                ">
                {{ getSpaceTitle(spaceId) }}
                <button type="button" @click="removeSpace(spaceId)" style="
                    all: unset;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 1;
                    color: var(--fcom-text-off, #8A8D91);
                  ">
                  ×
                </button>
              </span>
            </div>

            <p class="fcom_form_hint_block">{{ __('restrict_to_spaces_hint') }}</p>
          </div>

          <!-- Show in global feed (only when spaces selected) -->
          <div v-if="form.spaceIds.length > 0" class="fcom_form_group">
            <label class="fcom_checkbox_custom_label">
              <span class="fcom_cat_filter_check" :style="[
                { 'margin-top': '1px', 'flex-shrink': '0' },
                form.showInGlobal
                  ? 'background:var(--fcom-primary-button,#1877F2);border-color:var(--fcom-primary-button,#1877F2);'
                  : '',
              ]">
                <span v-if="form.showInGlobal" />
              </span>
              <input v-model="form.showInGlobal" type="checkbox" style="display: none" />
              <div>
                <span class="fcom_checkbox_label_text">
                  {{ __('hide_from_global_for_non_members') }}
                </span>
                <p class="fcom_form_hint_block">{{ __('hide_from_global_hint') }}</p>
              </div>
            </label>
          </div>

          <!-- Pin to sidebar -->
          <div class="fcom_form_group">
            <label class="fcom_checkbox_custom_label">
              <span class="fcom_cat_filter_check" :style="[
                { 'margin-top': '1px', 'flex-shrink': '0' },
                form.pinnedToSidebar
                  ? 'background:var(--fcom-primary-button,#1877F2);border-color:var(--fcom-primary-button,#1877F2);'
                  : '',
              ]">
                <span v-if="form.pinnedToSidebar" />
              </span>
              <input v-model="form.pinnedToSidebar" type="checkbox" style="display: none" />
              <div>
                <span class="fcom_checkbox_label_text">
                  📌 {{ __('pin_to_sidebar') }}
                </span>
                <p class="fcom_form_hint_block">{{ __('pin_to_sidebar_hint') }}</p>
              </div>
            </label>
          </div>
        </div>

      </div>
    </div>
  </div>

  <MediaLibraryModal
    v-model:open="showMediaLibrary"
    :selected-id="form.featuredImageId"
    @select="onSelectLibraryImage"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { DateTime } from 'luxon'
import { __ } from '@/utils/i18n.js'
import icons from '@/utils/icons.js'
import { api, eventsApi, categoriesApi, settingsApi, spacesApi, timezonesApi } from '@/utils/api.js'
import TipTapEditor from '@/components/TipTapEditor.vue'
import MediaLibraryModal from '@/components/MediaLibraryModal.vue'
import { isVideoMime, parseRestErrorMessage } from '@/utils/media.js'

// ── Props & emits ──────────────────────────────────────────────────────────────

const props = defineProps({
  eventId: { type: [String, Number], default: null },
  eventSlug: { type: String, default: null },
})

const emit = defineEmits(['saved', 'cancel'])

// ── Global config ──────────────────────────────────────────────────────────────

const config = window.FcaEventsConfig || {}

const showMediaLibrary = ref(false)

const canPickFromLibrary = computed(() => {
  if (!config.wpV2Base) return false
  if (Object.prototype.hasOwnProperty.call(config, 'canBrowseMedia')) {
    return Boolean(config.canBrowseMedia)
  }
  return true
})

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Ensure a URL has an https:// scheme if it lacks any scheme.
 */
function ensureHttps(url) {
  if (!url || url.match(/^https?:\/\//i)) return url
  return 'https://' + url
}

/**
 * Parse a date/datetime string stored in UTC, then set it to the given timezone.
 * Returns a Luxon DateTime or null.
 */
function parseDateInZone(dateStr, timezone) {
  if (!dateStr) return null
  const normalized = dateStr.replace(' ', 'T')
  const dt = DateTime.fromISO(normalized, { zone: 'utc' })
  return dt.isValid ? dt.setZone(timezone || 'UTC') : null
}

// ── State ──────────────────────────────────────────────────────────────────────

const isSubmitting = ref(false)
const lastSubmitTime = ref(0)
/** File upload only when WordPress media library is unavailable (no upload_files). */
const isUploadingFallback = ref(false)
const errorMessage = ref(null)
const categories = ref([])
const isLoadingCategories = ref(true)
const status = ref('publish')
const showNewCategoryForm = ref(false)
const newCategoryName = ref('')
const isCreatingCategory = ref(false)
const timezoneSettings = ref({ enabled_timezones: [] })
const allTimezones = ref({ UTC: 'UTC', 'Europe/London': 'Europe/London (GMT/BST)' })

const form = ref({
  title: '',
  slug: '',
  summary: '',
  content: '',
  date: '',
  time: '',
  endDate: '',
  endTime: '',
  timezone: 'Europe/London',
  eventType: 'in_person',
  location: '',
  eventUrl: '',
  meetingSpaceIds: [],
  /** `any` = member of any listed space; `all` = member of every listed space (meeting link). */
  meetingSpaceMemberMode: 'any',
  meetingProvider: '',
  meetingProviderOther: '',
  scheduleLinkVisibility: false,
  linkVisibleHoursBefore: 0,
  featuredImageId: null,
  featuredImageUrl: null,
  /** From attachment `post_mime_type` (e.g. image/jpeg, video/mp4). */
  featuredImageMime: null,
  categories: [],
  spaceIds: [],
  showInGlobal: false,
  pinnedToSidebar: false,
})

const spaces = ref([])
const isLoadingSpaces = ref(false)
const spaceDropdownOpen = ref(false)
const spaceMeetingLinkDropdownOpen = ref(false)
const spaceFilterRef = ref(null)
const spaceMeetingLinkFilterRef = ref(null)

// Space context (set from URL or global window vars)
const spaceContext = ref({
  isSpaceContext: false,
  spaceId: null,
  spaceTitle: null,
  spaceSlug: null,
})

// ── Computed ───────────────────────────────────────────────────────────────────

const isDraft = computed(() => status.value === 'draft')

const isFeaturedVideo = computed(() => isVideoMime(form.value.featuredImageMime))

const filteredTimezones = computed(() => {
  const enabled = timezoneSettings.value.enabled_timezones || []
  if (enabled.length === 0) return allTimezones.value
  const filtered = {}
  enabled.forEach((key) => {
    if (allTimezones.value[key]) {
      filtered[key] = allTimezones.value[key]
    }
  })
  return filtered
})

const spaceSelectionLabel = computed(() => {
  if (form.value.spaceIds.length === 0) return __('all_spaces_no_restriction')
  return form.value.spaceIds.length + ' ' + __('spaces_selected')
})

const spaceMeetingLinkSelectionLabel = computed(() => {
  if (form.value.meetingSpaceIds.length === 0) return __('all_spaces_no_restriction')
  return form.value.meetingSpaceIds.length + ' ' + __('spaces_selected')
})

const backLinkLabel = computed(() => {
  if (isDraft.value) return __('back_to_events')
  return props.eventId ? __('back_to_event') : __('back_to_events')
})

const submitButtonLabel = computed(() => {
  if (isSubmitting.value) {
    if (isDraft.value) return __('publishing')
    return props.eventId ? __('saving') : __('publishing')
  }
  if (isDraft.value) return __('publish_draft')
  return props.eventId ? __('save_changes') : __('publish')
})

// ── Watchers ───────────────────────────────────────────────────────────────────

// Auto-manage showInGlobal when spaces change
watch(
  () => form.value.spaceIds.length,
  (newLen, oldLen) => {
    if (newLen > 0 && oldLen === 0) {
      form.value.showInGlobal = false
    } else if (newLen === 0) {
      form.value.showInGlobal = true
    }
  },
)

watch(
  () => form.value.meetingSpaceIds.length,
  (len) => {
    if (len === 0) {
      form.value.meetingSpaceMemberMode = 'any'
    }
  },
)

// ── Methods ────────────────────────────────────────────────────────────────────

/**
 * Detect and store space context from URL or window globals.
 */
function detectSpaceContext() {
  const urlMatch = window.location.pathname.match(/\/space\/([^/]+)\/events/)
  if (urlMatch) {
    spaceContext.value.spaceSlug = urlMatch[1]
    spaceContext.value.isSpaceContext = true
    if (window.FcaSpaceContext) {
      spaceContext.value.spaceId = window.FcaSpaceContext.spaceId
      spaceContext.value.spaceTitle = window.FcaSpaceContext.spaceTitle
    }
    return
  }

  if (window.FcaSpaceCreateIntent && window.FcaSpaceCreateIntent.spaceId) {
    const intent = window.FcaSpaceCreateIntent
    window.FcaSpaceCreateIntent = null
    spaceContext.value.isSpaceContext = true
    spaceContext.value.spaceId = intent.spaceId
    spaceContext.value.spaceTitle = intent.spaceTitle
    spaceContext.value.spaceSlug = intent.spaceSlug
    if (!form.value.spaceIds.includes(intent.spaceId)) {
      form.value.spaceIds = [intent.spaceId]
    }
    if (!form.value.meetingSpaceIds.includes(intent.meetingSpaceIds)) {
      form.value.meetingSpaceIds = [intent.meetingSpaceIds]
    }
    return
  }

  if (
    window.FcaSpaceContext &&
    window.FcaSpaceContext.isSpaceEventsPage &&
    window.FcaSpaceContext.spaceId
  ) {
    spaceContext.value.isSpaceContext = true
    spaceContext.value.spaceId = window.FcaSpaceContext.spaceId
    spaceContext.value.spaceTitle = window.FcaSpaceContext.spaceTitle
    spaceContext.value.spaceSlug = window.FcaSpaceContext.spaceSlug
    if (!form.value.spaceIds.includes(window.FcaSpaceContext.spaceId)) {
      form.value.spaceIds = [window.FcaSpaceContext.spaceId]
    }
    if (!form.value.meetingSpaceIds.includes(window.FcaSpaceContext.meetingSpaceIds)) {
      form.value.meetingSpaceIds = [window.FcaSpaceContext.meetingSpaceIds]
    }
  }
}

/**
 * Generate a URL slug from the current title.
 */
function generateSlug() {
  let slug = (form.value.title || '').trim()

  const charMap = {
    ä: 'ae', Ä: 'ae', ö: 'oe', Ö: 'oe', ü: 'ue', Ü: 'ue', ß: 'ss',
    à: 'a', á: 'a', â: 'a', ã: 'a', å: 'a',
    è: 'e', é: 'e', ê: 'e', ë: 'e',
    ì: 'i', í: 'i', î: 'i', ï: 'i',
    ò: 'o', ó: 'o', ô: 'o', õ: 'o',
    ù: 'u', ú: 'u', û: 'u',
    ñ: 'n', ç: 'c', ð: 'd', ø: 'o', þ: 'th', æ: 'ae', œ: 'oe', ý: 'y', ÿ: 'y',
    ł: 'l', ń: 'n', ś: 's', ź: 'z', ż: 'z',
    č: 'c', ď: 'd', ě: 'e', ň: 'n', ř: 'r', š: 's', ť: 't', ů: 'u', ž: 'z',
  }

  slug = slug.replace(/./g, (ch) => charMap[ch] || charMap[ch.toLowerCase()] || ch)
  slug = slug.toLowerCase()
  slug = slug.replace(/[^a-z0-9\s-]/g, '')
  slug = slug.replace(/[\s]+/g, '-')
  slug = slug.replace(/-{2,}/g, '-').replace(/^-|-$/g, '')

  if (slug.length > 80) {
    slug = slug.substring(0, 80).replace(/-$/, '')
  }

  if (!slug) {
    slug = 'event-' + Date.now().toString(36)
  }

  form.value.slug = slug
}

/**
 * Handle featured image file selection and upload.
 */
/** Plugin upload endpoint (fallback when `canBrowseMedia` is false). */
async function handleImageUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return

  isUploadingFallback.value = true
  errorMessage.value = null

  const formData = new FormData()
  formData.append('file', file)

  try {
    const res = await fetch(`${config.apiBase}upload`, {
      method: 'POST',
      headers: { 'X-WP-Nonce': config.nonce },
      body: formData,
      credentials: 'same-origin',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(parseRestErrorMessage(data, __('upload_failed')))
    }
    const data = await res.json()
    form.value.featuredImageId = data.id
    form.value.featuredImageUrl = data.url
    form.value.featuredImageMime = data.mime || null
  } catch (err) {
    errorMessage.value = __('image_upload_failed') + ': ' + err.message
  } finally {
    isUploadingFallback.value = false
    if (event?.target) {
      event.target.value = ''
    }
  }
}

/**
 * Clear featured image for this form only (does not delete the file in the media library).
 */
function removeFeaturedLocal() {
  form.value.featuredImageId = null
  form.value.featuredImageUrl = null
  form.value.featuredImageMime = null
}

function openMediaLibrary() {
  if (!canPickFromLibrary.value) return
  showMediaLibrary.value = true
}

function onSelectLibraryImage({ id, url, mime }) {
  if (id) {
    form.value.featuredImageId = id
  }
  if (url) {
    form.value.featuredImageUrl = url
  }
  form.value.featuredImageMime = mime != null && mime !== '' ? mime : null
  // Modal sets `v-model:open` when a grid item is chosen (closes). "Add new" upload keeps the modal open.
}

/**
 * Load available categories from the API.
 */
async function loadCategories() {
  isLoadingCategories.value = true
  try {
    const res = await fetch(`${config.apiBase}categories`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (res.ok) {
      categories.value = await res.json()
    }
  } catch {
    // Non-fatal: categories simply won't be shown
  } finally {
    isLoadingCategories.value = false
  }
}

/**
 * Create a new category and add it to the selected list.
 */
async function createCategory() {
  if (!newCategoryName.value.trim()) return

  isCreatingCategory.value = true
  errorMessage.value = null

  try {
    const res = await fetch(`${config.apiBase}categories`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: newCategoryName.value.trim() }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || `HTTP ${res.status}`)
    }
    const newCat = await res.json()
    categories.value.push(newCat)
    form.value.categories.push(newCat.id)
    newCategoryName.value = ''
    showNewCategoryForm.value = false
  } catch (err) {
    errorMessage.value = __('failed_to_create_category') + ': ' + err.message
  } finally {
    isCreatingCategory.value = false
  }
}

/**
 * Cancel adding a new category.
 */
function cancelNewCategory() {
  showNewCategoryForm.value = false
  newCategoryName.value = ''
}

/**
 * Load available spaces from the Fluent Community REST API.
 */
async function loadSpaces() {
  isLoadingSpaces.value = true
  try {
    const restUrl = `${config.baseUrl}/wp-json/fluent-community/v2/`
    const nonce = window.appVars?.rest?.nonce || config.nonce
    const res = await fetch(restUrl.replace(/\/$/, '') + '/spaces/discover', {
      headers: { 'X-WP-Nonce': nonce },
    })
    if (res.ok) {
      const data = await res.json()
      spaces.value = data.spaces.data || []
    }
  } catch (err) {
    console.error('Failed to load spaces:', err)
  } finally {
    isLoadingSpaces.value = false
  }
}

/**
 * Find the title of a space by its ID.
 */
function getSpaceTitle(spaceId) {
  const space = spaces.value.find((s) => s.id === spaceId)
  return space?.title || spaceId
}

/**
 * Revove meeting space form the selected list.
 */
function removeMeetingLinkSpace(spaceId) {
  form.value.meetingSpaceIds = form.value.meetingSpaceIds.filter((id) => id !== spaceId)
}
/**
 * Remove a space from the selected list.
 */
function removeSpace(spaceId) {
  form.value.spaceIds = form.value.spaceIds.filter((id) => id !== spaceId)
}

/**
 * Load an existing event for editing.
 */
async function loadEvent() {
  if (!props.eventSlug && !props.eventId) return

  try {
    const path = props.eventId
      ? `${config.apiBase}events/${props.eventId}?full=true`
      : `${config.apiBase}events/${props.eventSlug}?full=true`
    const res = await fetch(path, { headers: { 'X-WP-Nonce': config.nonce } })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const event = await res.json()
    status.value = event.status || 'publish'
    populateForm(event)
  } catch (err) {
    errorMessage.value = __('failed_to_load_event') + ': ' + err.message
  }
}

/**
 * Populate the form fields from an event API response.
 */
function populateForm(event) {
  const startDt = event.date ? parseDateInZone(event.date, event.timezone) : null
  const endDt = event.end_time ? parseDateInZone(event.end_time, event.timezone) : null

  form.value.title = event.title || ''
  form.value.slug = event.slug || ''
  form.value.summary = event.summary || ''
  form.value.timezone = event.timezone || 'UTC'
  form.value.eventType = event.event_type || 'in_person'

  // Meeting provider
  const knownProviders = ['zoom', 'google_meet', 'microsoft_teams', 'webex', 'other', '']
  if (event.meeting_provider && !knownProviders.includes(event.meeting_provider)) {
    form.value.meetingProvider = 'other'
    form.value.meetingProviderOther = event.meeting_provider
  } else {
    form.value.meetingProvider = event.meeting_provider || ''
    form.value.meetingProviderOther = ''
  }

  // Link visibility — API now returns integer (0 = no restriction, >0 = hours before)
  const lvh = parseInt(event.link_visible_hours_before) || 0
  form.value.scheduleLinkVisibility = lvh > 0
  form.value.linkVisibleHoursBefore = lvh > 0 ? lvh : 1

  form.value.location = event.location || ''
  form.value.eventUrl = event.event_url || ''
  form.value.featuredImageId = event.image_id || null
  form.value.featuredImageUrl = event.image || null
  form.value.featuredImageMime = event.image_mime || null
  form.value.categories = (event.categories || []).map((c) => c.id || c)
  form.value.content = event.raw_content || event.content || ''
  form.value.spaceIds = (event.space_ids || []).map((id) => parseInt(id))
  form.value.meetingSpaceIds = (event.meeting_space_ids || []).map((id) => parseInt(id))
  form.value.meetingSpaceMemberMode = event.meeting_space_member_mode === 'all' ? 'all' : 'any'
  form.value.showInGlobal = event.hide_from_global !== 'yes'
  form.value.pinnedToSidebar = event.pinned_to_sidebar === 'yes'

  if (startDt) {
    form.value.date = startDt.toFormat('yyyy-MM-dd')
    form.value.time = startDt.toFormat('HH:mm')
  }
  if (endDt) {
    form.value.endDate = endDt.toFormat('yyyy-MM-dd')
    form.value.endTime = endDt.toFormat('HH:mm')
  }
}

/**
 * Submit the event form (create or update).
 * @param {'publish'|'draft'} submitStatus
 */
async function submitForm(submitStatus = 'publish') {
  // Debounce rapid double-submits
  const now = Date.now()
  if (now - lastSubmitTime.value < 2000) {
    console.log('[FCA Events] Submission debounced - too soon since last submit')
    return
  }
  lastSubmitTime.value = now

  if (!form.value.title || !form.value.date) {
    errorMessage.value = __('title_date_required')
    return
  }

  isSubmitting.value = true
  errorMessage.value = null

  try {
    const payload = {
      title: form.value.title,
      slug: form.value.slug || undefined,
      content: form.value.content,
      summary: form.value.summary,
      date: form.value.date,
      time: form.value.time,
      end_date: form.value.endDate || form.value.date,
      end_time: form.value.endTime || form.value.time,
      timezone: form.value.timezone,
      event_type: form.value.eventType,
      meeting_provider:
        form.value.meetingProvider === 'other'
          ? form.value.meetingProviderOther || 'Other'
          : form.value.meetingProvider,
      meeting_provider_other:
        form.value.meetingProvider === 'other'
          ? form.value.meetingProviderOther
          : '',
      link_visible_hours_before: form.value.scheduleLinkVisibility
        ? Math.max(1, parseInt(form.value.linkVisibleHoursBefore) || 1)
        : 0,
      location: form.value.location,
      event_url: ensureHttps(form.value.eventUrl),
      featured_image_id: form.value.featuredImageId,
      categories: form.value.categories.map(Number),
      space_ids: form.value.spaceIds.map(Number),
      meeting_space_ids: form.value.meetingSpaceIds.map(Number),
      meeting_space_member_mode: form.value.meetingSpaceMemberMode,
      hide_from_global: form.value.spaceIds.length > 0 && !form.value.showInGlobal ? 'yes' : 'no',
      pinned_to_sidebar: form.value.pinnedToSidebar ? 'yes' : 'no',
      status: submitStatus === 'draft' ? 'draft' : 'publish',
    }

    if (spaceContext.value.isSpaceContext && spaceContext.value.spaceId) {
      payload.space_id = spaceContext.value.spaceId
    }

    const url = props.eventId
      ? `${config.apiBase}events/${props.eventId}`
      : `${config.apiBase}events`

    const res = await fetch(url, {
      method: props.eventId ? 'PUT' : 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || `HTTP ${res.status}`)
    }

    const savedEvent = await res.json()
    window.dispatchEvent(new CustomEvent('fca-event-saved', { detail: savedEvent }))
    emit('saved', savedEvent)
  } catch (err) {
    errorMessage.value = err.message
  } finally {
    isSubmitting.value = false
  }
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────

onMounted(async () => {
  detectSpaceContext()

  // Update document title
  const siteTitle = window.fluentComAdmin?.site_title || document.title
  const isEdit = !!props.slug
  const pageTitle =
    spaceContext.value.isSpaceContext && spaceContext.value.spaceTitle
      ? __(isEdit ? 'edit_event' : 'create_event') + ' - ' + spaceContext.value.spaceTitle
      : __(isEdit ? 'edit_event' : 'create_event')
  document.title = pageTitle + ' - ' + siteTitle

  // Load data
  loadCategories()
  loadSpaces()

  // Close meeting link space dropdown when clicking outside
  document.addEventListener('click', (event) => {
    if (spaceMeetingLinkFilterRef.value && !spaceMeetingLinkFilterRef.value.contains(event.target)) {
      spaceMeetingLinkDropdownOpen.value = false
    }
  })
  // Close space dropdown when clicking outside
  document.addEventListener('click', (event) => {
    if (spaceFilterRef.value && !spaceFilterRef.value.contains(event.target)) {
      spaceDropdownOpen.value = false
    }
  })
  // Default date to today if not editing an existing event
  if (!form.value.date) {
    form.value.date = new Date().toISOString().split('T')[0]
  }

  // Load settings (timezone list, enabled timezones, default timezone)
  try {
    const settingsRes = await fetch(`${config.apiBase}settings`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (settingsRes.ok) {
      const settings = await settingsRes.json()
      if (settings.timezone_list) {
        allTimezones.value = settings.timezone_list
      }
      if (settings.enabled_timezones) {
        timezoneSettings.value.enabled_timezones = settings.enabled_timezones
      }

      // Attempt to load full timezone list from dedicated endpoint
      try {
        const tzRes = await fetch(`${config.apiBase}timezones`, {
          headers: { 'X-WP-Nonce': config.nonce },
        })
        if (tzRes.ok) {
          allTimezones.value = await tzRes.json()
        }
      } catch {
        // Fall back to timezone_list from settings
      }

      if (settings.default_timezone && !props.eventId) {
        form.value.timezone = settings.default_timezone
      }
    }
  } catch {
    // Non-fatal: timezone list defaults will be used
  }

  // Load existing event data for edit mode
  if (props.eventId || props.eventSlug) {
    await loadEvent()
  }
})
</script>

<style scoped>
.fcom_featured_image_has_image {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fcom_image_preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid var(--fcom-primary-border, #e5e7eb);
}
.fcom_image_preview img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: middle;
}
.fcom_featured_preview_video {
  display: block;
  width: 100%;
  max-height: 220px;
  background: #111;
}
.fcom_featured_image_links {
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.4;
}
.fcom_featured_image_link,
.fcom_featured_image_link_muted,
.fcom_upload_fallback {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: var(--fcom-text-link, #3b82f6);
  font-weight: 500;
  cursor: pointer;
}
.fcom_featured_image_link_muted {
  color: var(--fcom-text-muted, #6b7280);
}
.fcom_featured_image_link:hover,
.fcom_upload_fallback:hover {
  text-decoration: underline;
}
.fcom_featured_image_link_muted:hover {
  text-decoration: underline;
  color: #b91c1c;
}
.fcom_featured_image_sep {
  color: var(--fcom-text-muted, #9ca3af);
  margin: 0 6px;
}
.fcom_set_featured_image_btn {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--fcom-primary-border, #e5e7eb);
  background: var(--fcom-primary-bg, #fff);
  color: var(--fcom-text-link, #3b82f6);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
}
.fcom_set_featured_image_btn:hover {
  background: var(--fcom-secondary-bg, #f3f4f6);
}
.fcom_upload_fallback_block {
  display: block;
  cursor: pointer;
  border: 1px dashed var(--fcom-primary-border, #d1d5db);
  border-radius: 8px;
  padding: 14px;
  text-align: center;
}
.fcom_upload_fallback_inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--fcom-text, #111);
}
</style>
